import { createElement, useContext, useMemo } from "react";
import { PortableText } from "@portabletext/react";
import VideoEmbed from "../Video/Video.jsx";
import Button from "../Button/Button.jsx";
import { ModuleTime } from "../../../features/workshops/spark-overview/components/StageSchedule/ModuleLink/ModuleTime.jsx";
import { blockContentStyles as styles } from "./blockContent.css.js";
import GalleryBlock from "./components/GalleryBlock/GalleryBlock.jsx";
import CustomComponentsCtx from "./components/BranchBlock/CustomComponentsCtx.jsx";
import Branch from "./components/BranchBlock/BranchBlock.jsx";
import GeniallyIframe from "./components/GeniallyIframe/GeniallyIframe.jsx";
import ImageBlock from "./components/ImageBlock/ImageBlock.jsx";
import GlossaryEntry from "./components/GlossaryEntry/GlossaryEntry";
import { GLOSSARY_MARK_TYPE } from "./glossary";

const glossaryMark = ({ value, children }) => (
  <GlossaryEntry term={value?.term}>{children}</GlossaryEntry>
);

const Quote = ({ value }) => {
  const { customComponents } = useContext(CustomComponentsCtx);
  const { author, content, source } = value;

  let Credit = author;
  if (source) {
    Credit = (
      <a href={source} target="_blank" rel="noreferrer">
        {author}
      </a>
    );
  }

  return (
    <div className={styles.box}>
      <div className={styles.quoteContent}>
        <PortableText value={content} components={customComponents} />
      </div>
      <div className={styles.credit}>{Credit}</div>
    </div>
  );
};

const TimeBlock = ({ value }) => {
  const { minutes } = value;
  return (
    <span className={styles.duration}>
      <ModuleTime duration={minutes} />
    </span>
  );
};

/**
 * value = ["library", "showroom"]
 * @param value
 * @return {import("react").JSX.Element}
 * @constructor
 */
const GoToButton = ({ value }) => {
  const target = value.value;
  if (target !== "library" && target !== "showroom") {
    return <></>;
  }
  const isLibrary = target === "library";

  const link = isLibrary ? "../library" : "../showroom";
  const label = isLibrary ? "Bibliothek" : "Showroom";

  return (
    <Button
      to={link}
      width={"fit-content"}
      iconRight={isLibrary ? "library" : "showroom"}
    >
      {label}
    </Button>
  );
};

const MyDownloadButton = ({ value }) => {
  const { asset, label } = value.file;
  if (!asset)
    return <p className={styles.error}>Download button misses file!</p>;
  const [, name, ext] = asset._ref.split("-");
  const link = `https://cdn.sanity.io/files/${
    import.meta.env.VITE_SANITY_PROJECT_ID
  }/production/${name}.${ext}?dl=`;
  return (
    <Button onClick={() => window.open(link, "_blank")}>
      {label || "Download"}
    </Button>
  );
};

// Shared inline/list serializers, reused by the top-level renderer and StyledBox.
const linkMark = ({ children, value }) => (
  <a className={styles.link} href={value.href} target="_blank" rel="noreferrer">
    {children}
  </a>
);

const listComponents = {
  bullet: ({ children }) => <ul className={styles.ul}>{children}</ul>,
  number: ({ children }) => <ol className={styles.ol}>{children}</ol>,
};

const listItemComponents = {
  bullet: ({ children }) => <li className={styles.ulLi}>{children}</li>,
  number: ({ children }) => <li className={styles.olLi}>{children}</li>,
};

// Components used inside a StyledBox: blocks inherit the box's own text color
// (no textColor variant) so headings/paragraphs/lists read against the box bg.
const boxComponents = {
  block: ({ value, children }) => {
    const el = ["h2", "h3"].includes(value.style) ? value.style : "p";
    return createElement(
      el,
      { className: styles.block({ type: value.style || "normal" }) },
      children,
    );
  },
  marks: { link: linkMark, [GLOSSARY_MARK_TYPE]: glossaryMark },
  list: listComponents,
  listItem: listItemComponents,
};

const StyledBox = ({ value }) => {
  const { content, style } = value;
  const type = ["goal", "task", "summary", "learnMore"].includes(style)
    ? style
    : "goal";
  return (
    <div className={styles.styledBox({ type })}>
      <PortableText value={content} components={boxComponents} />
    </div>
  );
};

const getCustomComponents = ({ textColor = "dark" }) => ({
  block: ({ value, children }) => {
    const el = ["h2", "h3"].includes(value.style) ? value.style : "p";
    const isNormal = ["normal", "h2", "h3", undefined].includes(value.style);
    const className = styles.block({
      type: value.style || "normal",
      textColor: isNormal ? textColor : undefined,
    });
    return createElement(el, { className }, children);
  },
  types: {
    image: ImageBlock,
    complexImage: ImageBlock,
    video: ({ value }) => <VideoEmbed url={value.url} />,
    quote: Quote,
    branch: Branch,
    timeBlock: TimeBlock,
    downloadButton: MyDownloadButton,
    gallery: GalleryBlock,
    genially: ({ value }) => (
      <GeniallyIframe url={value.url} title={value.title} />
    ),
    goToButton: GoToButton,
    styledBox: StyledBox,
  },
  marks: {
    link: linkMark,
    [GLOSSARY_MARK_TYPE]: glossaryMark,
  },
  list: listComponents,
  listItem: listItemComponents,
});

/**
 * Renders Spark Content from Sanity.
 *
 * Glossary highlighting is applied upstream: callers pass already-marked
 * `content` (see `injectGlossaryMarks`) and provide `GlossaryDescriptionsCtx`
 * for the tooltips. This component only renders the `glossaryTerm` marks it
 * finds; it is otherwise glossary-agnostic.
 *
 * @param content
 * @param textColor
 * @return {import("react").JSX.Element}
 */
const BlockContent = ({ content, textColor = "dark" }) => {
  const ctxVal = useMemo(
    () => ({
      customComponents: getCustomComponents({ textColor }),
    }),
    [textColor],
  );
  return (
    <CustomComponentsCtx.Provider value={ctxVal}>
      <PortableText value={content} components={ctxVal.customComponents} />
    </CustomComponentsCtx.Provider>
  );
};

export default BlockContent;
